<template>
    <div class="filter-item-container">
        <a-select v-model:value="form.field"
            class="column-item" 
            placeholder="请选择字段"
            optionFilterProp="title"
            show-search
            :disabled="disableAll"
            @change="changeField">
            <template v-for="item in columnList" :key="item.javaField">
                <a-select-option :value="item.javaField"
                    :disabled="selectedJavaFields.includes(item.javaField)"
                    :title="item.columnComment">
                    {{ item.columnComment }}
                </a-select-option>
            </template>
        </a-select>
        <a-select v-model:value="form.rule"
            class="rule-item" 
            placeholder="请选择"
            :disabled="disableAll || ruleDisabled"
            optionFilterProp="title"
            show-search
            @change="changeRule">
            <template v-for="item in ruleOptions" :key="item.value">
                <a-select-option :value="item.value"
                    :title="item.label">
                    {{ item.label }}
                </a-select-option>
            </template>
        </a-select>        
        <component :is="customComp" class="result-item"
            v-model:value="form.val"
            :selectMode="selectMode"
            :key="selectedColumn.javaField"
            :needLabel="needLabel"
            :rule="form.rule"
            :disabled="disableAll || form.disabled"
            placeholder="请输入"
            :column="selectedColumn"
            :class="{'hide-component': hideComponent}"
            @input="changeInput"
            @change="changeResult">
        </component>
    </div>
</template>

<script lang="ts" setup name="filterItem">
import { computed, reactive, ref, shallowRef, watch, nextTick, onMounted } from 'vue';
import { getDictItemsByCode } from '/@/utils/dict';
import { SubmitParams, HtmlType, valCanBeNullRules, FilterLabels } from '../config';
import { Input } from 'ant-design-vue';
import FilterSelect from './filterSelect.vue'
import FilterTime from './filterTime.vue'
import FilterCommonTree from './filterCommonTree.vue'
import FilterAmount from './filterAmount.vue'
import FilterTree from './filterTree.vue'
import { UserSelect } from "/@/ecnComponents/UserSelect";

const props = withDefaults(
    defineProps<{
        columnList: Array<any>,
        value: SubmitParams,
        disableAll?: boolean,
        needLabel?: boolean,
        filterArr?: Array<any>,
        limitFieldSingle?: boolean,
    }>(), {
        columnList: () => [],
        value: () => ({}),
        disableAll: false,
        needLabel: false,
        filterArr: () => [],
        limitFieldSingle: false,
    }
)
const emit = defineEmits(['update:value', 'change']);
const filterLabels: FilterLabels = {
    fieldLabel: '',
    ruleLabel: '',
    valueLabel: '',
}

const selectedJavaFields = computed(() => {
    if (!props.limitFieldSingle) {
        return [];
    }
    return props.filterArr.map(item => item.field).filter(item => item !== form.field)
})

const customComp = shallowRef(Input);

const form = reactive({
    field: null,
    rule: null,
    val: null,
})

onMounted(() => {
  initPosterFlag(form.field)
})

const hideComponent = computed(() => {
    return valCanBeNullRules.includes(form.rule)
})

const ruleDisabled = ref(false)
const selectedColumn = ref({})
const allRuleOptions = getDictItemsByCode('filter_type') || [];
const ruleOptions = ref([])

const selectMode = computed(() => {
    if (selectedColumn.value?.optionMaxNum === 1) {
        return '';
    }

    return 'multiple'
});

watch(() => props.value, (value) => {
    form.field = value.field;
    changeRuleConfigAccordingField();
    changeCompAccordingField();
    value.rule && (form.rule = value.rule);
    form.val = value.val;
    setLabels()
}, {
    immediate: true,
})

function setLabels() {
    if (!props.needLabel) {
        return;
    }
    filterLabels.fieldLabel = props.columnList?.find(columnItme => columnItme.javaField === form.field)?.columnComment;
    filterLabels.ruleLabel = ruleOptions.value.find(ruleItme => ruleItme.value === form.rule)?.label;
}

function changeField(value) {
    form.field = value;

    changeRuleConfigAccordingField();
    changeRule(ruleOptions.value[0]?.value)
    changeCompAccordingField();

    //如果是规则是执行人，需要写死配置
    nextTick(() => {
      initPosterFlag(value)
    })
}

function initPosterFlag(value) {
  if(value === "prjPosterFlag") {
    form.val = "通过项目海报二维码加入项目"
    form.disabled = true
    emitChange()
  } else {
    form.disabled = false
  }
}

function changeRuleConfigAccordingField() {
    selectedColumn.value = props.columnList.find(item => item.javaField === form.field) || {};
    const availableRules = selectedColumn.value.queryType?.split?.(',') || [];
    ruleOptions.value = allRuleOptions.filter(item => availableRules.includes(item.value));
    ruleDisabled.value = ruleOptions.value.length === 1
    form.rule = ruleOptions.value[0]?.value;
}

function changeRule(value) {
    form.rule = value;
    changeResult(null)
    setLabels();
}

function changeResult(value) {
    if (selectedColumn.value.htmlType === HtmlType.input) {
        form.val = value?.currentTarget?.value ?? value;
    } else {
        form.val = value;
    }
    emitChange()
}

function changeInput(value) {//特殊字符处理
  if (selectedColumn.value.htmlType === HtmlType.input) {
    if(selectedColumn.value.javaField === 'initiatorName') {
      form.val = value?.currentTarget?.value?.replace(/[^\w\u4E00-\u9FA5]/g, '') ?? value?.replace(/[^\w\u4E00-\u9FA5]/g, '');
      emitChange()
    }
  }
}

function emitChange() {
    const result = {
        ...props.value,
        ...form,
        ...selectedColumn.value,
    }
    if (props.needLabel) {
        result.filterLabels = filterLabels;
    }
    emit('update:value', result)
    emit('change', result)
}

function changeCompAccordingField() {
    const compMap = {
        [HtmlType.input]: Input,
        [HtmlType.select]: FilterSelect,
        [HtmlType.dateTime]: FilterTime,
        [HtmlType.area]: FilterTree,
        [HtmlType.tree]: FilterTree,
        [HtmlType.commonTree]: FilterCommonTree,
        [HtmlType.amount]: FilterAmount,
        [HtmlType.userSelect]: UserSelect,
    }
    
    customComp.value = compMap[selectedColumn.value.htmlType] || Input;
}


</script>


<style lang="less" scoped>
@warning-color: #F4A21F;
.filter-item-container {
    display: flex;
    align-items: center;
}
.column-item {
    width: 160px;
    margin-right: 8px;
}
.rule-item {
    width: 100px;
    margin-right: 8px;
}

.result-item {
    flex: 1;
    width: 0;
}

.hide-component {
    visibility: hidden;
}
.filter-item-container:has(.ant-form-item-explain-error) {
    .column-item, .rule-item {
        margin-bottom: 24px;
    }
}
:deep(.ant-form-inline .ant-form-item-with-help) {
    margin-bottom: 0!important;
}
:deep(.ant-form-item-has-error .ant-input-number, .ant-form-item-has-error .ant-picker) {
    border-color: @warning-color;
}
:deep(.ant-form-item-has-error .ant-input-number-focused, .ant-form-item-has-error .ant-picker-focused, .ant-form-item-has-error .ant-input-number:focus, .ant-form-item-has-error .ant-picker:focus) {
    border-color: @warning-color!important;
}
:deep(.ant-form-item-has-error .ant-input-number:not([disabled]):hover) {
    border-color: @warning-color!important;
}
:deep(.ant-form-item-has-error .ant-picker:not([disabled]):hover) {
    border-color: @warning-color!important;
}
:deep(.ant-form-item-explain.ant-form-item-explain-error) {
    color: @warning-color;
}
</style>
